// src/components/Navbar.js
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserDoc } from '../hooks/useUserDoc';
import { SvgIcon } from './Icon';
import { FULL_ACCESS_MODE } from '../config/flags';

const navBtn = {
  padding: '5px 9px', fontSize: '0.72rem', fontWeight: 600, borderRadius: 8,
  border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)',
  cursor: 'pointer', fontFamily: 'Noto Sans, sans-serif', textDecoration: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s', lineHeight: 1.3, whiteSpace: 'nowrap',
};

const menuItem = {
  display: 'block', width: '100%', padding: '12px 20px', fontSize: '0.86rem',
  fontWeight: 500, color: 'var(--text)', textDecoration: 'none', fontFamily: 'Noto Sans, sans-serif',
  borderBottom: '1px solid var(--border)', background: 'none', border: 'none',
  borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'var(--border)',
  textAlign: 'left', cursor: 'pointer',
};

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { isPremium, isBusiness, userDoc } = useUserDoc();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();

  const toggleLang = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('lang', newLang);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const closeMenu = () => setMenuOpen(false);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [menuOpen]);

  const firstName = user?.displayName?.split(' ')[0] || '';

  return (
    <nav className="navbar" ref={menuRef}>
      <Link to="/" className="navbar-brand" onClick={closeMenu}>
        <img
          src="/images/tutto-logo-transparent.png"
          alt="TuttoClaro"
          style={{ height: 36, width: 'auto', objectFit: 'contain' }}
        />
        {isBusiness && userDoc?.businessName ? userDoc.businessName : 'TuttoClaro'}
      </Link>

      {/* Desktop nav */}
      <div className="navbar-actions">
        {user ? (
          <>
            <span className="navbar-greeting">{t('nav.hello', { name: firstName })}</span>
            <Link to="/dashboard" style={navBtn}>{t('nav.myAgreements')}</Link>
            <Link to="/account" style={navBtn}>{t('nav.account')}</Link>
            <button onClick={handleLogout} style={navBtn}>{t('nav.logOut')}</button>
          </>
        ) : (
          <>
            {!FULL_ACCESS_MODE && <Link to="/pricing" style={navBtn}>{t('nav.pricing')}</Link>}
            <Link to="/login" style={{ ...navBtn, background: 'var(--primary)', color: 'white', border: '1.5px solid var(--primary)' }}>{t('nav.logIn')}</Link>
          </>
        )}
        <button onClick={toggleLang} style={{ ...navBtn, fontSize: '0.68rem', padding: '4px 7px' }}>{i18n.language === 'en' ? 'ES' : 'EN'}</button>
      </div>

      {/* Mobile: lang toggle + hamburger */}
      <div className="navbar-mobile-right">
        <button onClick={toggleLang} style={{ ...navBtn, fontSize: '0.68rem', padding: '4px 7px' }}>{i18n.language === 'en' ? 'ES' : 'EN'}</button>
        <button
          className="navbar-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
          style={{ display: 'inline-flex' }}
        >
          {menuOpen ? '\u2715' : '\u2630'}
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', borderBottom: '2px solid var(--border)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          {user ? (
            <>
              <div style={{ ...menuItem, color: 'var(--muted)', fontSize: '0.78rem', fontWeight: 400, cursor: 'default' }}>
                {t('nav.hello', { name: firstName })}
              </div>
              <Link to="/dashboard" style={menuItem} onClick={closeMenu}>{t('nav.myAgreements')}</Link>
              <Link to="/account" style={menuItem} onClick={closeMenu}>{t('nav.account')}</Link>
              <Link to="/support" style={menuItem} onClick={closeMenu}>{t('nav.support')}</Link>
              <button onClick={handleLogout} style={{ ...menuItem, color: 'var(--declined)', borderBottom: 'none' }}>{t('nav.logOut')}</button>
            </>
          ) : (
            <>
              {!FULL_ACCESS_MODE && <Link to="/pricing" style={menuItem} onClick={closeMenu}>{t('nav.pricing')}</Link>}
              <Link to="/support" style={menuItem} onClick={closeMenu}>{t('nav.support')}</Link>
              <Link to="/login" style={{ ...menuItem, color: 'var(--primary)', fontWeight: 600, borderBottom: 'none' }} onClick={closeMenu}>{t('nav.logIn')}</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
